import { Stack } from '@mui/material';

import { type HandoverSpec } from '@/designs-dev/handover/types';
import Note from '@/designs-dev/annotations/Note';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';

export const v1IntakeSpec: HandoverSpec = {
  id: 'v1-intake',
  title: 'Intake, profile and review',
  description: '',
  links: [
    {
      label: 'Mini-PRD (Notion)',
      url: 'https://app.notion.com/p/karehero/Intake-profile-and-review-V1-mini-PRD-3ca63e8971dd81759aebf0384b5c3f4e',
    },
    {
      label: 'Prototype (Tell Us Once)',
      url: '/tell-us-once/intake-profile-review',
    },
  ],
  flows: [
    {
      title: 'A basic flow',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            {/* strong rather than a weight in sx: the emphasis is in the markup,
                and Figma has no semibold body to reach for. */}
            <span>
              <strong>
                The minimum we would need for this to be useful. Not the final
                designs. It is here to show the thinking and where it went.
              </strong>
            </span>
            <span>
              A light triage, so we know who we are asking about. Then a word
              dump, where they say what is happening in any order, typed or
              spoken.
            </span>
            <span>
              Then we show them what we read and they confirm it, and they
              review the claim before it goes anywhere.
            </span>
            <span>
              Then it has somewhere to live: the claims it fills, and a profile
              that keeps it.
            </span>
            <span>
              What the two flows differ on is where the questions get asked.
            </span>
            <span>
              It follows the tech plan: what we take from what they tell us is a
              set of facts, and a fact has to be reviewed by a person before it
              counts.
            </span>
            <span>
              No live conversation yet. Nothing here is talking to a model in
              real time, and the wording of every reply is written.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'youre-in',
          title: "You're in",
          render: () => <Flow initial='youre-in' />,
        },
        {
          id: 'ai-consent',
          title: 'Before we start',
          tap: "Continue when you're done",
          render: () => <Flow initial='ai-consent' />,
          alt: {
            title: 'Or they say no',
            render: () => <Flow initial='ai-consent-declined' />,
          },
          note: (
            <Note title='Consent' variant='body2'>
              Both options read what they write with a model, so both ask here,
              before anything is asked. Two answers rather than one Continue: a
              consent that cannot be refused is a notice. Where no leads is
              still open.
            </Note>
          ),
        },
        {
          id: 'supporting',
          title: 'Are you supporting someone?',
          tap: "Continue when you're done",
          render: () => <Flow initial='supporting' />,
        },
        {
          id: 'caring-for',
          title: 'Who are you caring for?',
          tap: 'Pick a fork option, then Next',
          render: () => <Flow initial='caring-for' />,
        },
        {
          id: 'chat',
          title: 'Tell us about it',
          tap: 'Next',
          render: () => <Flow initial='chat' />,
        },
        {
          id: 'legal-name',
          title: 'Your name',
          tap: 'Answer, or skip',
          render: () => <Flow initial='legal-name' />,
        },
        {
          id: 'review-all',
          title: 'Review everything, by confidence',
          tap: 'Next',
          render: () => <Flow initial='review-all' />,
        },
        {
          id: 'care-profile',
          title: 'Details, claims, docs, calls',
          tap: 'Done',
          render: () => <Flow initial='care-profile' option={2} />,
        },
      ],
    },
    {
      title: 'A chat style flow',
      note: (
        <Note title='Readme' colour='sunflower' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              <strong>
                The same flow, asked in a chat. Still not a live conversation.
              </strong>
            </span>
            <span>
              It does what the basic flow does. It is trying to feel more human,
              and to make handing something over easier: a photo, a voice note,
              or a sentence they typed.
            </span>
            <span>
              So this is where the input patterns get worked out. What has to be
              typed, what is better as a photo, and what we can take as a word
              dump and read afterwards.
            </span>
            <span>
              It runs as tell us about the person you are caring for, then a
              pause while we read it, then tell us about you.
            </span>
            <span>
              Then it shows what that filled in. That is the triage into the
              app, the prompt to do a bit more, and the way into everything else
              we offer.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'youre-in-v2',
          title: "You're in",
          render: () => <Flow initial='youre-in' option={2} />,
        },
        {
          id: 'ai-consent-v2',
          title: 'Before we start',
          tap: "Continue when you're done",
          render: () => <Flow initial='ai-consent' option={2} />,
          alt: {
            title: 'Or they say no',
            render: () => <Flow initial='ai-consent-declined' option={2} />,
          },
          note: (
            <Note title='Consent' variant='body2'>
              Both options read what they write with a model, so both ask here,
              before anything is asked. Two answers rather than one Continue: a
              consent that cannot be refused is a notice. Where no leads is
              still open.
            </Note>
          ),
        },
        {
          id: 'chat-v2-carer-name',
          title: 'Confirm your name',
          tap: "'I'm happy with that'",
          render: () => <Flow initial='chat-v2-carer-name' option={2} />,
        },
        {
          id: 'chat-v2-preferred-name',
          title: "Anything you'd prefer to be called",
          tap: "Tap 'Yes, that's me'",
          render: () => <Flow initial='chat-v2-preferred-name' option={2} />,
        },
        {
          id: 'chat-v2-mobile',
          title: 'Your mobile number',
          tap: "Tap 'No, Vez is fine'",
          render: () => <Flow initial='chat-v2-mobile' option={2} />,
        },
        {
          id: 'chat-v2',
          title: 'Are you supporting someone?',
          tap: 'Type a name',
          render: () => <Flow initial='chat-v2-chosen' option={2} />,
          alt: {
            title: 'Or they need care themselves',
            render: () => <Flow initial='chat-v2-chosen-self' option={2} />,
          },
        },
        {
          id: 'chat-v2-who',
          title: 'Their full name',
          tap: 'Type a mobile number',
          render: () => <Flow initial='chat-v2-who' option={2} />,
          alt: {
            title: 'Or their own name',
            render: () => <Flow initial='chat-v2-who-self' option={2} />,
          },
        },
        {
          id: 'chat-v2-situation',
          title: 'Tell us about it',
          tap: 'Type a full name, or upload a document',
          render: () => <Flow initial='chat-v2-situation' option={2} />,
          alt: {
            title: 'Placeholder',
            render: () => <Flow initial='self-branch' option={2} />,
          },
        },
        {
          id: 'chat-v2-recording',
          title: 'Recording',
          tap: 'Tap the orb',
          render: () => <Flow initial='chat-v2-recording' option={2} />,
        },
        {
          id: 'chat-v2-voice',
          title: 'Heard back before it sends',
          tap: 'Press it again',
          render: () => <Flow initial='chat-v2-voice' option={2} />,
        },
        {
          id: 'chat-v2-more',
          title: 'Send as many as you want',
          tap: 'Send',
          render: () => <Flow initial='chat-v2-more' option={2} />,
        },
        {
          id: 'chat-v2-stuck',
          title: 'Five notes later, the way on is still there',
          tap: 'Keep sending',
          render: () => <Flow initial='chat-v2-stuck' option={2} />,
        },
        {
          id: 'filled',
          title: 'What that filled in',
          tap: "Continue when you're done",
          render: () => <Flow initial='filled' option={2} />,
        },
        {
          id: 'calculator',
          title: 'Benefits calculator',
          tap: 'Tap the calculator',
          render: () => <Flow initial='calculator' option={2} />,
        },
        {
          id: 'eligible',
          title: 'What the calculator found',
          tap: "Continue when you're done",
          render: () => <Flow initial='eligible' option={2} />,
        },
        {
          id: 'review',
          title: 'Review Attendance Allowance',
          tap: 'Start the claim',
          render: () => <Flow initial='review' option={2} />,
        },
        {
          id: 'book-call',
          title: 'Talk it through with a care expert',
          tap: 'Finish',
          render: () => <Flow initial='book-call' option={2} />,
        },
        {
          id: 'care-profile-v2',
          title: 'Details, claims, docs, calls',
          tap: 'Book, or skip',
          render: () => <Flow initial='care-profile' option={2} />,
          note: (
            <Note title='Care for' variant='body2'>
              Here only if it outlives one claim: reused, changes over time, or
              a care expert needs it. Anything worded the way a form judges it
              stays in that claim.
            </Note>
          ),
        },
      ],
    },
  ],
  // Named once here, because every one of them is a decision the next flow will
  // have to make again.
  patterns: [
    {
      // The one desktop frame in here. A column rather than a stretched phone:
      // whether the conversation survives that is the question worth asking.
      id: 'chat-desktop',
      name: 'The chat on a desktop',
      group: 'ask',
      screen: 'desktop',
      render: () => <Flow initial='chat-desktop' option={2} />,
    },
    {
      // Was called a hard gate, when it was a radio list over a Next in a bar
      // of its own. It still gates — there is no typing past it — but it gates
      // in the conversation now.
      id: 'hard-gate',
      name: 'Answers as rows',
      group: 'ask',
      render: () => <Flow initial='chat-v2-fork' option={2} />,
    },
    {
      id: 'typed-only',
      name: 'Typed only',
      group: 'ask',
      render: () => <Flow initial='chat-v2-who' option={2} />,
    },
    {
      id: 'open-capture',
      name: 'Open capture',
      group: 'ask',
      render: () => <Flow initial='chat-v2-situation' option={2} />,
    },
    {
      // The other kind of row: lime rather than teal, and it leaves the
      // question rather than answering it. The only one left on this screen
      // now that uploading is something the question says rather than offers.
      id: 'in-chat-action',
      name: 'The way on',
      group: 'answer',
      render: () => <Flow initial='chat-v2-more' option={2} />,
    },
    {
      id: 'heard-back',
      name: 'Heard back before it sends',
      group: 'answer',
      render: () => <Flow initial='chat-v2-voice' option={2} />,
    },
    {
      id: 'keep-sending',
      name: 'Send as many as you want',
      group: 'answer',
      render: () => <Flow initial='chat-v2-stuck' option={2} />,
    },
    {
      id: 'review-row',
      name: 'The review row',
      group: 'read',
      render: () => <Flow initial='review' option={2} />,
    },
    {
      id: 'lands',
      name: 'Somewhere to live',
      group: 'keep',
      render: () => <Flow initial='care-profile' option={2} />,
    },
    {
      id: 'placeholder',
      name: 'Not designed yet',
      group: 'keep',
      render: () => <Flow initial='self-branch' option={2} />,
    },
  ],
  states: [],
  components: {},
};
